<template>
    <div class="bill-inquiry">
        <!-- 헤더 -->
        <div class="content-header">
            <h3>
                <i class="fas fa-search-dollar"></i>
                요금 조회
            </h3>
            <!-- 검색 바 -->
            <div class="search-bar-header">
                <div class="form-field-inline">
                    <label>공급자</label>
                    <select v-model="filterSupplier" class="form-select-header" @change="handleSearch">
                        <option value="">전체</option>
                        <option
                            v-for="supplier in uniqueSuppliers"
                            :key="supplier.supplierIdx"
                            :value="supplier.supplierIdx"
                        >
                            {{ supplier.supplierName }}
                        </option>
                    </select>
                </div>

                <div class="form-field-inline">
                    <label>기간</label>
                    <div class="date-range-group-header">
                        <div
                            class="date-picker-container-header"
                            v-click-outside="closeStartDatePicker"
                            @click="toggleStartDatePicker"
                        >
                            <div class="date-display-header">
                                <span>{{ startDateDisplay }}</span>
                                <i :class="['fas fa-chevron-down', { rotate: showStartDatePicker }]"></i>
                            </div>

                            <!-- 드롭다운 -->
                            <div v-if="showStartDatePicker" class="date-dropdown">
                                <div class="year-selector">
                                    <button @click.stop="changeStartYear(-1)">
                                        <i class="fas fa-chevron-left"></i>
                                    </button>
                                    <span>{{ startDateYear }}</span>
                                    <button @click.stop="changeStartYear(1)">
                                        <i class="fas fa-chevron-right"></i>
                                    </button>
                                </div>
                                <div class="month-grid">
                                    <button
                                        v-for="month in 12"
                                        :key="month"
                                        :class="['month-btn', { active: month === startDateMonth }]"
                                        @click.stop="selectStartMonth(month)"
                                    >
                                        {{ month }}월
                                    </button>
                                </div>
                            </div>
                        </div>
                        <span class="date-range-separator">~</span>
                        <div
                            class="date-picker-container-header"
                            v-click-outside="closeEndDatePicker"
                            @click="toggleEndDatePicker"
                        >
                            <div class="date-display-header">
                                <span>{{ endDateDisplay }}</span>
                                <i :class="['fas fa-chevron-down', { rotate: showEndDatePicker }]"></i>
                            </div>

                            <!-- 드롭다운 -->
                            <div v-if="showEndDatePicker" class="date-dropdown">
                                <div class="year-selector">
                                    <button @click.stop="changeEndYear(-1)">
                                        <i class="fas fa-chevron-left"></i>
                                    </button>
                                    <span>{{ endDateYear }}</span>
                                    <button @click.stop="changeEndYear(1)">
                                        <i class="fas fa-chevron-right"></i>
                                    </button>
                                </div>
                                <div class="month-grid">
                                    <button
                                        v-for="month in 12"
                                        :key="month"
                                        :class="['month-btn', { active: month === endDateMonth }]"
                                        @click.stop="selectEndMonth(month)"
                                    >
                                        {{ month }}월
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="form-field-inline">
                    <button class="btn-search-header" @click="handleSearch">
                        <i class="fas fa-search"></i>
                        검색
                    </button>
                </div>

                <div class="form-field-inline">
                    <button class="btn-reset-header" @click="resetSearch">
                        <i class="fas fa-redo"></i>
                        초기화
                    </button>
                </div>
            </div>
        </div>

        <!-- 요금 목록 테이블 -->
        <div class="data-table-container">
            <table class="settings-data-table">
                <thead>
                    <tr>
                        <th>계약명</th>
                        <th>공급자</th>
                        <th>요금제</th>
                        <th>집계년월</th>
                        <th>사용량</th>
                        <th>총 요금</th>
                        <th>작업</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="bill in filteredBills"
                        :key="bill.billIdx"
                        class="clickable-row"
                        @click="openDetailView(bill)"
                    >
                        <td>
                            <div class="point-info">
                                <div class="point-name">{{ bill.contract?.contractName || "-" }}</div>
                            </div>
                        </td>
                        <td>
                            <span class="supplier-text">{{ bill.supplier?.supplierName || "-" }}</span>
                        </td>
                        <td>
                            <span class="cost-text">{{ bill.cost?.costName || "-" }}</span>
                        </td>
                        <td>
                            <span class="ym-text">{{ formatYearMonth(bill.regYm) }}</span>
                        </td>
                        <td class="text-right">
                            <span class="usage-text"
                                >{{ formatNumber(bill.rawUsage, 0) }} {{ bill.rawUnit || "-" }}</span
                            >
                        </td>
                        <td class="text-right">
                            <span class="fee-text">{{ formatNumber(bill.totalFee, 0) }}원</span>
                        </td>
                        <td class="text-center" @click.stop>
                            <button class="btn-detail" @click="openDetailView(bill)">
                                <i class="fas fa-eye"></i>
                                상세
                            </button>
                            <button class="btn-delete" @click="handleDelete(bill)">
                                <i class="fas fa-trash-alt"></i>
                                삭제
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- 데이터 없음 -->
            <div v-if="filteredBills.length === 0" class="no-data">
                <i class="fas fa-inbox"></i>
                <p>조회된 요금 정보가 없습니다.</p>
            </div>
        </div>

        <!-- 요금 상세 패널 (슬라이드) -->
        <SettingsSidePanel :show="showDetail" title="요금 상세" size="default" @close="closeDetail">
            <!-- 기본 정보 -->
            <div class="info-section">
                <h5 class="section-title">기본 정보</h5>
                <div class="info-grid">
                    <div class="info-item">
                        <span class="info-label">공급자</span>
                        <span class="info-value">{{ billDetail.supplier?.supplierName || "-" }}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">요금제</span>
                        <span class="info-value">{{ billDetail.cost?.costName || "-" }}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">계약명</span>
                        <span class="info-value">{{ billDetail.contract?.contractName || "-" }}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">집계년월</span>
                        <span class="info-value">{{ formatYearMonth(billDetail.regYm) }}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">청구 기간</span>
                        <span class="info-value">
                            {{ formatBillingDate(billDetail.billingStartAt) || "-" }} ~
                            {{ formatBillingDate(billDetail.billingEndAt) || "-" }}
                        </span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">청구 단위</span>
                        <span class="info-value">{{ billDetail.billingUnit || "-" }}</span>
                    </div>
                </div>
            </div>

            <!-- 사용량 정보 -->
            <div class="info-section compact">
                <div class="usage-info-compact">
                    <div class="usage-info-row">
                        <span class="usage-info-label">사용량</span>
                        <span class="usage-info-value"
                            >{{ formatNumber(billDetail.rawUsage, 0) }} {{ billDetail.rawUnit || "-" }}</span
                        >
                    </div>
                    <div class="usage-info-row different">
                        <span class="usage-info-label">청구 사용량</span>
                        <span class="usage-info-value"
                            >{{ formatNumber(billDetail.billingUsage, 0) }} {{ billDetail.billingUnit || "-" }}</span
                        >
                    </div>
                    <div class="usage-info-row highlight">
                        <span class="usage-info-label">단위 금액</span>
                        <span class="usage-info-value">
                            {{ formatNumber(billDetail.unitPrice, 2) }}원/{{ billDetail.rawUnit || "-" }}
                        </span>
                    </div>
                </div>
            </div>

            <!-- 요금 계산 방식 -->
            <div v-if="billDetail.calcDetails" class="info-section">
                <h5 class="section-title">요금 계산 방식</h5>
                <div class="calc-method-card">
                    <div class="method-row">
                        <span class="method-label">기본요금 계산 방식</span>
                        <span class="method-value">{{
                            getBaseFeeCalcTypeName(billDetail.calcDetails.baseFeeCalcType)
                        }}</span>
                    </div>
                    <div class="method-row">
                        <span class="method-label">사용요금 계산 방식</span>
                        <div class="structure-badges">
                            <span v-if="billDetail.calcDetails.hasProgressive === 1" class="structure-badge active">
                                <i class="fas fa-check"></i>
                                누진제
                            </span>
                            <span v-else class="structure-badge inactive"> 누진제 </span>
                            <span v-if="billDetail.calcDetails.hasTimeSlot === 1" class="structure-badge active">
                                <i class="fas fa-check"></i>
                                시간대
                            </span>
                            <span v-else class="structure-badge inactive"> 시간대 </span>
                            <span v-if="billDetail.calcDetails.hasSeason === 1" class="structure-badge active">
                                <i class="fas fa-check"></i>
                                계절
                            </span>
                            <span v-else class="structure-badge inactive"> 계절 </span>
                            <span v-if="billDetail.calcDetails.hasDayType === 1" class="structure-badge active">
                                <i class="fas fa-check"></i>
                                요일
                            </span>
                            <span v-else class="structure-badge inactive"> 요일 </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 요금 내역 -->
            <div class="info-section">
                <h5 class="section-title">요금 내역</h5>
                <div class="fee-grid">
                    <div class="fee-row">
                        <span class="fee-label">기본요금</span>
                        <span class="fee-value">{{ formatNumber(billDetail.baseFee, 0) }}원</span>
                    </div>
                    <div class="fee-row">
                        <span class="fee-label">사용량요금</span>
                        <span class="fee-value">{{ formatNumber(billDetail.usageFee, 0) }}원</span>
                    </div>
                    <div class="fee-row highlight">
                        <span class="fee-label">기타요금</span>
                        <span class="fee-value">{{ formatNumber(billDetail.etcFee, 0) }}원</span>
                    </div>
                    <div class="fee-row subtotal">
                        <span class="fee-label">소계</span>
                        <span class="fee-value">{{ formatNumber(billDetail.subTotal, 0) }}원</span>
                    </div>
                    <div class="fee-row">
                        <span class="fee-label">부가세</span>
                        <span class="fee-value">{{ formatNumber(billDetail.vatAmount, 0) }}원</span>
                    </div>
                    <div class="fee-row discount">
                        <span class="fee-label"
                            >할인 ({{ formatNumber(billDetail.contract?.discountRate || 0, 2) }}%)</span
                        >
                        <span class="fee-value">-{{ formatNumber(billDetail.discountAmount, 0) }}원</span>
                    </div>
                </div>
                <div class="fee-row total">
                    <span class="fee-label">총 요금</span>
                    <span class="fee-value">{{ formatNumber(billDetail.totalFee, 0) }}원</span>
                </div>
            </div>

            <!-- 기타요금 상세 -->
            <div v-if="billDetail.etcFeeDetails && billDetail.etcFeeDetails.length > 0" class="info-section">
                <h5 class="section-title">기타요금 상세</h5>
                <div class="etc-fee-list">
                    <div
                        v-for="(detail, index) in billDetail.etcFeeDetails"
                        :key="detail.detailIdx || index"
                        class="etc-fee-item"
                    >
                        <div class="etc-fee-header">
                            <div class="etc-fee-name">{{ detail.etcCostName }}</div>
                            <div class="etc-fee-amount">{{ formatNumber(detail.amount, 0) }}원</div>
                        </div>
                        <div class="etc-fee-calc">
                            <template v-if="detail.calcType === 'USAGE'">
                                {{ formatNumber(detail.unitPrice, 0) }}원 ×
                                {{ formatNumber(billDetail.billingUsage, 0) }}
                                {{ billDetail.billingUnit || "-" }}
                            </template>
                            <template v-else-if="detail.calcType === 'FIXED'">
                                {{ formatNumber(detail.fixedAmount, 0) }}원 (고정금액)
                            </template>
                            <template v-else-if="detail.calcType === 'TOTALCOST'">
                                {{ formatNumber(detail.rate, 2) }}% × {{ formatNumber(billDetail.subTotal, 0) }}원
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 등록 정보 -->
            <div class="detail-info">
                <div class="info-row">
                    <span class="info-label">등록일시</span>
                    <span class="info-value">{{ billDetail.regDate || "-" }}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">등록자</span>
                    <span class="info-value">{{ billDetail.regWorker || "-" }}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">수정일시</span>
                    <span class="info-value">{{ billDetail.modDate || "-" }}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">수정자</span>
                    <span class="info-value">{{ billDetail.modWorker || "-" }}</span>
                </div>
            </div>
            <template #footer>
                <button class="btn-delete-large" @click="handleDeleteFromDetail">
                    <i class="fas fa-trash-alt"></i>
                    삭제
                </button>
                <button class="btn-close" @click="closeDetail">
                    <i class="fas fa-times"></i>
                    닫기
                </button>
            </template>
        </SettingsSidePanel>
    </div>
</template>

<script>
import SettingsSidePanel from "../settings/SettingsSidePanel.vue";
import backEndApi from "@src/api/backEndApi.js";
import * as XLSX from "xlsx";

export default {
    name: "BillInquiry",
    components: {
        SettingsSidePanel,
    },
    directives: {
        clickOutside: {
            bind(el, binding) {
                el.clickOutsideEvent = function (event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.body.addEventListener("click", el.clickOutsideEvent);
            },
            unbind(el) {
                document.body.removeEventListener("click", el.clickOutsideEvent);
            },
        },
    },
    data() {
        return {
            filterPoint: "",
            filterSupplier: "",
            filterStartYm: "",
            filterEndYm: "",
            showDetail: false,
            billDetail: {},
            bills: [],
            // 시작일 날짜 선택기
            showStartDatePicker: false,
            startDateYear: new Date().getFullYear(),
            startDateMonth: 1,
            // 종료일 날짜 선택기
            showEndDatePicker: false,
            endDateYear: new Date().getFullYear(),
            endDateMonth: new Date().getMonth() + 1,
        };
    },
    computed: {
        startDateDisplay() {
            if (this.filterStartYm) {
                const [year, month] = this.filterStartYm.split("-");
                return `${year}년 ${parseInt(month)}월`;
            }
            return `${this.startDateYear}년 ${this.startDateMonth}월`;
        },
        endDateDisplay() {
            if (this.filterEndYm) {
                const [year, month] = this.filterEndYm.split("-");
                return `${year}년 ${parseInt(month)}월`;
            }
            return `${this.endDateYear}년 ${this.endDateMonth}월`;
        },
        filteredBills() {
            let filtered = [...this.bills];

            // 관제점 필터링
            if (this.filterPoint) {
                filtered = filtered.filter((b) => b.ptIdx === parseInt(this.filterPoint));
            }

            // 공급자 필터링
            if (this.filterSupplier) {
                filtered = filtered.filter((b) => b.supplierIdx === parseInt(this.filterSupplier));
            }

            return filtered;
        },
        uniqueSuppliers() {
            const supplierMap = new Map();
            this.bills.forEach((bill) => {
                if (bill.supplier) {
                    supplierMap.set(bill.supplier.supplierIdx, bill.supplier);
                }
            });
            return Array.from(supplierMap.values()).sort((a, b) => a.supplierName.localeCompare(b.supplierName));
        },
    },
    mounted() {
        this.setDefaultDateRange();
        this.loadData();
    },
    // 설정 모달 keep-alive: 다른 탭에서 바뀐 목록만 다시 받고 입력·결과는 유지
    activated() {
        if (!this.wasDeactivated) return;
        this.loadData();
    },
    deactivated() {
        this.wasDeactivated = true;
    },
    methods: {
        setDefaultDateRange() {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, "0");

            // 시작: 3개월 전
            const startDate = new Date(year, now.getMonth() - 3, 1);
            const startYear = startDate.getFullYear();
            const startMonth = "01";
            this.filterStartYm = `${startYear}-${startMonth}`;

            // 종료: 현재 월
            this.filterEndYm = `${year}-${month}`;

            // 날짜 선택기 초기화
            this.startDateYear = startYear;
            this.startDateMonth = 1;
            this.endDateYear = year;
            this.endDateMonth = now.getMonth() + 1;
        },
        // 시작일 날짜 선택기 메서드
        toggleStartDatePicker() {
            this.showStartDatePicker = !this.showStartDatePicker;
            if (this.showStartDatePicker && this.filterStartYm) {
                const [year, month] = this.filterStartYm.split("-");
                this.startDateYear = parseInt(year);
                this.startDateMonth = parseInt(month);
            }
        },
        closeStartDatePicker() {
            this.showStartDatePicker = false;
        },
        changeStartYear(direction) {
            this.startDateYear += direction;
            this.updateStartDate();
        },
        selectStartMonth(month) {
            this.startDateMonth = month;
            this.showStartDatePicker = false;
            this.updateStartDate();
        },
        updateStartDate() {
            const year = String(this.startDateYear);
            const month = String(this.startDateMonth).padStart(2, "0");
            this.filterStartYm = `${year}-${month}`;
        },
        // 종료일 날짜 선택기 메서드
        toggleEndDatePicker() {
            this.showEndDatePicker = !this.showEndDatePicker;
            if (this.showEndDatePicker && this.filterEndYm) {
                const [year, month] = this.filterEndYm.split("-");
                this.endDateYear = parseInt(year);
                this.endDateMonth = parseInt(month);
            }
        },
        closeEndDatePicker() {
            this.showEndDatePicker = false;
        },
        changeEndYear(direction) {
            this.endDateYear += direction;
            this.updateEndDate();
        },
        selectEndMonth(month) {
            this.endDateMonth = month;
            this.showEndDatePicker = false;
            this.updateEndDate();
        },
        updateEndDate() {
            const year = String(this.endDateYear);
            const month = String(this.endDateMonth).padStart(2, "0");
            this.filterEndYm = `${year}-${month}`;
        },

        convertToYYYYMM(ymMonth) {
            if (!ymMonth) return "";
            return ymMonth.replace("-", "");
        },

        async loadData() {
            try {
                const params = {
                    ptIdx: this.filterPoint || null,
                    startYm: this.convertToYYYYMM(this.filterStartYm),
                    endYm: this.convertToYYYYMM(this.filterEndYm),
                };

                const result = await backEndApi.energyBillCalc.getBillList(params);
                if (result.status === 200) {
                    this.bills = result.data;
                } else {
                    this.$notify({
                        type: "warning",
                        title: "경고",
                        text: result.data.message,
                    });
                }
            } catch (error) {
                console.error("요금 목록 조회 실패:", error);
                this.$notify({
                    type: "error",
                    title: "오류",
                    text: "요금 목록을 불러오는데 실패했습니다.",
                });
            }
        },

        async loadDetailData(billIdx) {
            try {
                const result = await backEndApi.energyBillCalc.getBill(billIdx);
                if (result.status === 200) {
                    this.billDetail = result.data;
                } else {
                    this.$notify({
                        type: "warning",
                        title: "경고",
                        text: result.data.message,
                    });
                }
            } catch (error) {
                console.error("요금 상세 조회 실패:", error);
                this.$notify({
                    type: "error",
                    title: "오류",
                    text: "요금 상세 조회에 실패했습니다.",
                });
            }
        },

        handleSearch() {
            this.loadData();
        },

        resetSearch() {
            this.filterPoint = "";
            this.filterSupplier = "";
            this.setDefaultDateRange();
            this.loadData();
        },

        async openDetailView(bill) {
            await this.loadDetailData(bill.billIdx);
            this.showDetail = true;
        },

        closeDetail() {
            this.showDetail = false;
            this.billDetail = {};
        },

        async handleDelete(bill) {
            const confirm = await this.alertConfirmDanger(
                `${bill.point?.ptName || ""} (${this.formatYearMonth(bill.regYm)}) 요금 정보를 삭제하시겠습니까?`,
            );
            if (confirm.value) {
                try {
                    const result = await backEndApi.energyBillCalc.deleteBill(bill.billIdx);
                    if (result.status === 200) {
                        await this.alertNoti("삭제되었습니다.");
                        this.loadData();
                    } else {
                        await this.alertWarning(result.data.message);
                    }
                } catch (error) {
                    console.error("요금 삭제 실패:", error);
                    await this.alertWarning("요금 삭제에 실패했습니다.");
                }
            }
        },

        async handleDeleteFromDetail() {
            const confirm = await this.alertConfirmWarning(
                `${this.billDetail.point?.ptName || ""} (${this.formatYearMonth(this.billDetail.regYm)}) 요금 정보를 삭제하시겠습니까?`,
            );
            if (confirm.value) {
                try {
                    const result = await backEndApi.energyBillCalc.deleteBill(this.billDetail.billIdx);
                    if (result.status === 200) {
                        await this.alertNoti("삭제되었습니다.");
                        this.closeDetail();
                        this.loadData();
                    } else {
                        await this.alertWarning(result.data.message);
                    }
                } catch (error) {
                    console.error("요금 삭제 실패:", error);
                    await this.alertWarning("요금 삭제에 실패했습니다.");
                }
            }
        },

        exportToExcel() {
            try {
                const excelData = this.filteredBills.map((bill, index) => ({
                    순번: index + 1,
                    관제점명: bill.point?.ptName || "-",
                    관제점주소: bill.point?.ptAddr || "-",
                    공급자: bill.supplier?.supplierName || "-",
                    요금제: bill.cost?.costName || "-",
                    집계년월: this.formatYearMonth(bill.regYm),
                    사용량: bill.rawUsage || "0",
                    "사용량 단위": bill.rawUnit || "-",
                    최대수요: bill.maxDemand || "0",
                    기본요금: bill.baseFee || "0",
                    사용량요금: bill.usageFee || "0",
                    기타요금: bill.etcFee || "0",
                    소계: bill.subTotal || "0",
                    할인: bill.discountAmount || "0",
                    부가세: bill.vatAmount || "0",
                    총요금: bill.totalFee || "0",
                }));

                const worksheet = XLSX.utils.json_to_sheet(excelData);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "요금 목록");

                const columnWidths = [
                    { wch: 8 }, // 순번
                    { wch: 20 }, // 관제점명
                    { wch: 20 }, // 관제점주소
                    { wch: 15 }, // 공급자
                    { wch: 20 }, // 요금제
                    { wch: 10 }, // 집계년월
                    { wch: 12 }, // 총사용량
                    { wch: 12 }, // 최대수요
                    { wch: 12 }, // 기본요금
                    { wch: 12 }, // 사용량요금
                    { wch: 12 }, // 연료비조정
                    { wch: 12 }, // 기후환경요금
                    { wch: 12 }, // 전력기금
                    { wch: 12 }, // 소계
                    { wch: 12 }, // 할인
                    { wch: 12 }, // 부가세
                    { wch: 12 }, // 총요금
                ];
                worksheet["!cols"] = columnWidths;

                const today = new Date();
                const dateStr = today.toISOString().split("T")[0].replace(/-/g, "");
                const fileName = `요금목록_${dateStr}.xlsx`;

                XLSX.writeFile(workbook, fileName);

                this.$notify({
                    type: "success",
                    title: "성공",
                    text: "엑셀 파일이 다운로드되었습니다.",
                });
            } catch (error) {
                console.error("엑셀 다운로드 실패:", error);
                this.$notify({
                    type: "error",
                    title: "오류",
                    text: "엑셀 파일 다운로드에 실패했습니다.",
                });
            }
        },

        formatYearMonth(ym) {
            if (!ym) return "-";
            return `${ym.substring(0, 4)}년 ${ym.substring(4, 6)}월`;
        },

        formatNumber(value, decimals = 2) {
            return new Intl.NumberFormat("ko-KR", {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            }).format(value || 0);
        },

        // 청구 기간 날짜 포맷팅 (YYYYMMDD → YYYY-MM-DD)
        formatBillingDate(dateStr) {
            if (!dateStr) return "-";
            if (dateStr.length === 8) {
                return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
            }
            return dateStr;
        },

        // 계산 방식 이름 변환
        getCalcTypeName(calcType) {
            const typeMap = {
                USAGE: "요율",
                FIXED: "금액",
            };
            return typeMap[calcType] || calcType;
        },

        hasTimeSlotData(bill) {
            return bill.timeSlotUsage && (bill.timeSlotUsage.LOW || bill.timeSlotUsage.MID || bill.timeSlotUsage.PEAK);
        },

        getBaseFeeCalcTypeName(type) {
            if (!type) return "-";
            const types = {
                Fixed: "고정금액",
                Contract: "계약용량",
                // Demand: "수요기반",
                PeakLinked: "피크연동제",
                None: "없음",
            };
            return types[type] || type;
        },
    },
};
</script>

<style scoped>
.bill-inquiry {
    height: 100%;
    display: flex;
    flex-direction: column;
}

/* 헤더 */
.content-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--ebc-hairline);
}

.content-header h3 {
    font-size: 15px;
    font-weight: 700;
    color: var(--ebc-text);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.content-header h3 i {
    color: var(--ebc-primary);
}

.header-actions-group {
    display: flex;
    gap: 8px;
}

.btn-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-excel {
    background: white;
    color: var(--ebc-positive);
    border-color: var(--ebc-positive);
}

.btn-excel:hover {
    background: var(--ebc-positive-tint);
}

/* 헤더 안의 검색 바 */
.search-bar-header {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    justify-content: flex-end;
}

.search-bar-header .form-field-inline {
    display: flex;
    align-items: center;
    gap: 8px;
}

.search-bar-header .form-field-inline label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text);
    white-space: nowrap;
    margin: 0;
}

.form-select-header {
    padding: 6px 10px;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    font-size: 12px;
    transition: all 0.2s;
    min-width: 120px;
    height: 32px;
    background: var(--ebc-surface);
    cursor: pointer;
}

.form-select-header:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 2px var(--ebc-primary-tint);
}

.date-range-group-header {
    display: flex;
    align-items: center;
    gap: 6px;
}

.date-range-separator {
    color: var(--ebc-text-sub);
    font-weight: 600;
    font-size: 12px;
}

.date-picker-container-header {
    position: relative;
    cursor: pointer;
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    z-index: 100;
    min-width: 120px;
    height: 32px;
}

.date-display-header {
    padding: 6px 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    min-width: 120px;
    height: 100%;
    box-sizing: border-box;
    border-radius: var(--ebc-radius-sm);
    transition: all 0.2s ease;
    color: var(--ebc-text);
    font-weight: 600;
    font-size: 12px;
}

.date-display-header:hover {
    background: var(--ebc-track);
}

.date-display-header i {
    font-size: 10px;
    transition: transform 0.2s ease;
}

.date-display-header i.rotate {
    transform: rotate(180deg);
}

.btn-search-header {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: none;
    border-radius: var(--ebc-radius-xs);
    background: var(--ebc-primary);
    color: white;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-search-header:hover {
    background: var(--ebc-primary-hover);
}

.btn-reset-header {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    background: white;
    color: var(--ebc-text-sub);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-reset-header:hover {
    background: var(--ebc-track);
}

/* 날짜 선택기 스타일 (년월만 선택) */
.date-picker-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: var(--ebc-surface);
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    padding: 0;
    z-index: 100;
    height: 100%;
    min-height: 38px;
}

.date-display {
    padding: 10px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 120px;
    width: 100%;
    justify-content: center;
    border-radius: var(--ebc-radius-sm);
    transition: all 0.2s ease;
    color: var(--ebc-text);
    font-weight: 600;
    font-size: 13px;
    height: 100%;
    box-sizing: border-box;
}

.date-display:hover {
    background: var(--ebc-track);
}

.date-display i {
    font-size: 10px;
    transition: transform 0.2s ease;
}

.date-display i.rotate {
    transform: rotate(180deg);
}

.date-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-md);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    z-index: 101;
    margin-top: 6px;
    padding: 16px;
    min-width: 240px;
}

.year-selector {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--ebc-border);
}

.year-selector button {
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    padding: 4px 8px;
    cursor: pointer;
    color: var(--ebc-text-sub);
    border-radius: var(--ebc-radius-xs);
    transition: all 0.2s ease;
    font-weight: 600;
    font-size: 12px;
}

.year-selector button:focus {
    outline: none;
}

.year-selector button:hover {
    background: var(--ebc-primary);
    color: white;
    border-color: var(--ebc-primary);
}

.year-selector span {
    font-size: 14px;
    font-weight: 700;
    color: var(--ebc-text);
}

.month-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
}

.month-btn {
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    padding: 8px 4px;
    cursor: pointer;
    border-radius: var(--ebc-radius-xs);
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-text-sub);
    transition: all 0.2s ease;
    text-align: center;
}

.month-btn:hover {
    background: var(--ebc-border);
    border-color: var(--ebc-border-strong);
    color: var(--ebc-text);
}

.month-btn.active {
    background: var(--ebc-primary);
    border-color: var(--ebc-primary);
    color: white;
    box-shadow: 0 1px 4px var(--ebc-primary-tint-strong);
}

.btn-search,
.btn-reset {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-search {
    background: var(--ebc-primary);
    color: white;
    border-color: var(--ebc-primary);
}

.btn-search:hover {
    background: var(--ebc-primary-hover);
}

.btn-reset {
    background: white;
    color: var(--ebc-text-sub);
}

.btn-reset:hover {
    background: var(--ebc-track);
}

/* 데이터 테이블 */
.data-table-container {
    flex: 1;
    border: 1px solid var(--ebc-hairline);
    border-radius: var(--ebc-radius-md);
    overflow-y: auto;
    position: relative;
}

.settings-data-table {
    width: 100%;
    border-collapse: collapse;
}

.settings-data-table thead {
    background: var(--ebc-surface-sunken);
}

.settings-data-table th {
    padding: 12px 16px;
    text-align: left;
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text-sub);
    border-bottom: 1px solid var(--ebc-hairline);
    position: sticky;
    top: 0;
    background: var(--ebc-surface-sunken);
    z-index: 10;
}

.settings-data-table tbody tr {
    border-bottom: 1px solid var(--ebc-track);
    transition: all 0.2s;
}

.settings-data-table tbody tr:hover {
    background: var(--ebc-surface-sunken);
}

.settings-data-table td {
    padding: 14px 16px;
    font-size: 13px;
    color: var(--ebc-text);
}

.point-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.point-name {
    font-weight: 600;
    color: var(--ebc-text);
}

.point-addr {
    font-size: 11px;
    color: var(--ebc-text-sub);
}

.supplier-text {
    font-weight: 500;
    color: var(--ebc-text);
}

.cost-text {
    font-weight: 500;
    color: var(--ebc-text);
}

.ym-text {
    font-weight: 600;
    color: var(--ebc-text);
}

.usage-text {
    font-weight: 600;
    color: var(--ebc-text);
}

.fee-text {
    font-weight: 700;
    color: var(--ebc-text);
}

.btn-detail {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: var(--ebc-btn-h-sm);
    padding: 0 10px;
    min-width: 44px;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    background: white;
    border: 1px solid var(--ebc-border-strong);
    border-radius: var(--ebc-radius-xs);
    color: var(--ebc-text);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    margin-right: 4px;
}

.btn-detail:hover {
    background: var(--ebc-track);
    border-color: var(--ebc-text-muted);
}

.btn-delete {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: var(--ebc-btn-h-sm);
    padding: 0 10px;
    min-width: 44px;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    background: white;
    border: 1px solid var(--ebc-danger);
    border-radius: var(--ebc-radius-xs);
    color: var(--ebc-danger);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-delete:hover {
    background: var(--ebc-danger-tint);
}

.clickable-row {
    cursor: pointer;
}

.text-center {
    text-align: center !important;
}

.text-right {
    text-align: right !important;
}

/* 데이터 없음 */
.no-data {
    text-align: center;
    padding: 60px 20px;
    color: var(--ebc-text-muted);
}

.no-data i {
    font-size: 28px;
    margin-bottom: 16px;
    opacity: 0.5;
}

.no-data p {
    margin: 0;
    font-size: 14px;
}

/* 테이블 푸터 */
.table-footer {
    margin-top: 12px;
    padding: 12px 16px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-sm);
}

.total-count {
    font-size: 13px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

/* 상세 패널 */

/* 정보 섹션 */
.info-section {
    margin-bottom: 24px;
    padding: 20px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-sm);
    border: 1px solid var(--ebc-border);
}

.section-title {
    margin: 0 0 16px 0;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--ebc-hairline);
    font-size: 13px;
    font-weight: 700;
    color: var(--ebc-text);
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
}

.info-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.detail-info {
    margin-top: 24px;
    padding: 12px 16px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-sm);
    border: 1px solid var(--ebc-border);
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
}

.info-row {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: none;
}

.info-row:last-child {
    border-bottom: none;
}

.info-label {
    font-size: 11px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.info-value {
    font-size: 13px;
    font-weight: 600;
    color: var(--ebc-text);
}

.info-value.addr {
    font-size: 12px;
}

/* 청구 기간 */
.billing-period {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 12px;
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-hairline);
    border-radius: var(--ebc-radius-md);
    font-size: 13px;
    font-weight: 600;
    color: var(--ebc-text);
}

.billing-period i {
    color: var(--ebc-primary);
    font-size: 12px;
}

/* 사용량 정보 (컴팩트) */
.usage-info-compact {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.usage-info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    transition: all 0.2s;
}

.usage-info-row:hover {
    background: var(--ebc-surface-sunken);
}

.usage-info-row.different {
    border-color: var(--ebc-warn);
    background: var(--ebc-warn-tint);
}

.usage-info-row.highlight {
    border-color: var(--ebc-primary);
    background: var(--ebc-primary-tint);
}

.usage-info-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.usage-info-value {
    font-size: 13px;
    font-weight: 700;
    color: var(--ebc-text);
}

.usage-info-row.different .usage-info-value {
    color: var(--ebc-warn);
}

.usage-info-row.highlight .usage-info-value {
    color: var(--ebc-primary);
}

/* 단위 변환 정보 */
.info-section.compact {
    padding: 12px 16px;
}

.unit-conversion-info {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    font-size: 12px;
    color: var(--ebc-text-sub);
}

.unit-conversion-info i {
    color: var(--ebc-primary);
    font-size: 14px;
}

.conversion-badge {
    padding: 2px 8px;
    background: var(--ebc-track);
    color: var(--ebc-text);
    border-radius: var(--ebc-radius-xs);
    font-size: 10px;
    font-weight: 700;
    margin-left: auto;
}

/* 시간대별 테이블 */
.timeslot-table-wrapper {
    overflow-x: auto;
}

.timeslot-table {
    width: 100%;
    border-collapse: collapse;
}

.timeslot-table thead {
    background: var(--ebc-surface-sunken);
}

.timeslot-table th {
    padding: 10px 12px;
    text-align: center;
    font-size: 11px;
    font-weight: 700;
    color: var(--ebc-text-sub);
    border-bottom: 1px solid var(--ebc-hairline);
}

.timeslot-table td {
    padding: 10px 12px;
    text-align: center;
    font-size: 12px;
    color: var(--ebc-text);
    border-bottom: 1px solid var(--ebc-track);
}

.load-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: var(--ebc-radius-md);
    font-size: 10px;
    font-weight: 700;
}

.load-badge.low,
.load-badge.mid,
.load-badge.peak {
    background: var(--ebc-track);
    color: var(--ebc-text);
}

/* 요금 계산 방식 */
.calc-method-card {
    background: var(--ebc-surface-sunken);
    border: 1px solid var(--ebc-hairline);
    border-radius: var(--ebc-radius-md);
    padding: 16px;
}

.method-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid var(--ebc-border);
}

.method-row:last-child {
    border-bottom: none;
}

.method-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.method-value {
    font-size: 13px;
    font-weight: 700;
    color: var(--ebc-text);
}

.structure-badges {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.structure-badge {
    padding: 4px 10px;
    border-radius: var(--ebc-radius-md);
    font-size: 10px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 4px;
}

.structure-badge.active,
.structure-badge.inactive {
    background: var(--ebc-track);
    color: var(--ebc-text);
}

.structure-badge i {
    font-size: 10px;
}

/* 요금 내역 */

.fee-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
}

.fee-row {
    display: flex;
    justify-content: space-between;
    padding: 12px;
    background: white;
    border-radius: var(--ebc-radius-xs);
    border: 1px solid var(--ebc-border);
}

.fee-row.subtotal {
    background: var(--ebc-warn-tint);
    border-color: var(--ebc-warn);
    font-weight: 600;
}

.fee-row.discount {
    background: var(--ebc-positive-tint);
    border-color: var(--ebc-positive);
}

.fee-row.total {
    background: var(--ebc-primary);
    color: white;
    font-size: 16px;
    font-weight: 700;
    padding: 16px;
    margin-top: 8px;
}

.fee-row.total .fee-label,
.fee-row.total .fee-value {
    color: white;
}

.fee-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.fee-value {
    font-size: 14px;
    font-weight: 700;
    color: var(--ebc-text);
}

/* 푸터 */

.btn-delete-large {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-danger);
    border-radius: var(--ebc-radius-xs);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    background: white;
    color: var(--ebc-danger);
}

.btn-delete-large:hover {
    background: var(--ebc-danger-tint);
}

.btn-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    background: white;
    color: var(--ebc-text-sub);
}

.btn-close:hover {
    background: var(--ebc-track);
}

/* 기타요금 상세 */
.etc-fee-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 12px;
}

.etc-fee-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px 16px;
    background: white;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    transition: all 0.2s;
}

.etc-fee-item:hover {
    border-color: var(--ebc-primary);
}

.etc-fee-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.etc-fee-name {
    font-size: 14px;
    font-weight: 700;
    color: var(--ebc-text);
    flex: 1;
}

.etc-fee-amount {
    font-size: 16px;
    font-weight: 700;
    color: var(--ebc-primary);
    white-space: nowrap;
}

.etc-fee-calc {
    font-size: 12px;
    color: var(--ebc-text-sub);
    padding: 6px 10px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-xs);
    word-break: keep-all;
    line-height: 1.5;
}
</style>
